import { issueListFiltersSchema } from "@qualtio-workhub/contracts";
import {
  Badge,
  Button,
  Card,
  Input,
  Select,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@qualtio-workhub/ui";
import { useRouter, useRouterState } from "@tanstack/react-router";
import { Plus } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { listAgents, listContextUsers, listIssues } from "../../shared/api/client";
import { useWorkhubQuery } from "../../shared/api/hooks";
import { useAuth } from "../../shared/auth/auth-provider";
import { QueryFeedback } from "../../shared/components/query-feedback";
import { useI18n } from "../../shared/i18n/provider";
import { CreateIssueModal } from "./create-issue-modal";
import {
  type IssueListSearch,
  type IssueSort,
  issueListSearchParams,
  issueViews,
  readIssueListSearch,
  selectIssueView,
  sortIssueList,
} from "./issue-list-model";
import { IssueTable } from "./issue-widgets";
import {
  openIssueCount,
  priorityLabelKeys,
  priorityOptions,
  statusLabelKeys,
  statusOptions,
} from "./model";

export function IssueListPage() {
  const router = useRouter();
  const auth = useAuth();
  const { t } = useI18n();
  const searchStr = useRouterState({ select: (state) => state.location.searchStr });
  const search = useMemo(() => readIssueListSearch(searchStr), [searchStr]);
  const [createOpen, setCreateOpen] = useState(false);
  const { view = "all", sort = "updated", direction = "desc" } = search;
  const filters = useMemo(() => issueListFiltersSchema.parse(search), [search]);
  const loadIssues = useCallback((token: string) => listIssues(token, filters), [filters]);
  const issuesQuery = useWorkhubQuery(loadIssues);
  const usersQuery = useWorkhubQuery(listContextUsers);
  const agentsQuery = useWorkhubQuery(listAgents);
  const issues = useMemo(
    () =>
      sortIssueList(selectIssueView(issuesQuery.data ?? [], view, auth.user?.id), sort, direction),
    [issuesQuery.data, view, auth.user?.id, sort, direction],
  );
  const hasFilters = Object.keys(filters).length > 0 || view !== "all";
  const hasContextFilters = Object.keys(filters).some(
    (key) => !["q", "status", "priority"].includes(key),
  );
  const hasMetrics = issuesQuery.data !== null && !issuesQuery.error;

  function updateSearch(patch: Partial<IssueListSearch>, replace = false) {
    const next = readIssueListSearch(issueListSearchParams({ ...search, ...patch }));
    void router.navigate({ to: "/issues", search: next, replace, resetScroll: false });
  }

  function clearFilters() {
    void router.navigate({
      to: "/issues",
      search: readIssueListSearch(issueListSearchParams({ sort, direction })),
      resetScroll: false,
    });
  }

  function changeSort(next: IssueSort) {
    updateSearch({ sort: next, direction: next === sort && direction === "desc" ? "asc" : "desc" });
  }

  return (
    <section className="page-frame work-page issue-list-page">
      <div className="page-heading">
        <div>
          <Badge tone="accent">{t("work.meta")}</Badge>
          <h1>{t("work.issues.title")}</h1>
          <p className="muted-copy">{t("work.issues.subtitle")}</p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus size={16} />
          {t("issue.create.submit")}
        </Button>
      </div>
      <Card aria-busy={issuesQuery.loading} className="work-panel issue-list-panel">
        <Tabs
          className="issue-list-views"
          value={view}
          onValueChange={(value) => updateSearch({ view: value as IssueListSearch["view"] })}
        >
          <TabsList aria-label={t("issue.views.label")} variant="line">
            {issueViews.map((value) => (
              <TabsTrigger key={value} value={value}>
                {t(`issue.views.${value}`)}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value={view}>
            <div className="issue-list-panel-head">
              <div className="saved-view-row issue-list-stats">
                <Badge tone="neutral">
                  {t("work.metric.open")} {hasMetrics ? openIssueCount(issues) : "—"}
                </Badge>
                <Badge tone="neutral">
                  {t("work.metric.mine")}{" "}
                  {hasMetrics
                    ? issues.filter((issue) => issue.assigneeUserId === auth.user?.id).length
                    : "—"}
                </Badge>
                <Badge tone="warning">
                  {t("work.metric.urgent")}{" "}
                  {hasMetrics ? issues.filter((issue) => issue.priority === "urgent").length : "—"}
                </Badge>
                <Badge tone="accent">
                  {t("work.metric.agent")}{" "}
                  {hasMetrics
                    ? issues.filter((issue) => issue.assigneeAgentId !== null).length
                    : "—"}
                </Badge>
              </div>
            </div>
            <div className="filter-row issue-filter-row">
              <Input
                aria-label={t("search.global")}
                placeholder={t("issue.filter.search")}
                value={search.q ?? ""}
                onChange={(event) =>
                  updateSearch({ q: event.currentTarget.value || undefined }, true)
                }
              />
              <Select
                aria-label={t("issue.field.status")}
                value={search.status ?? ""}
                onChange={(event) =>
                  updateSearch({
                    status: (event.currentTarget.value as IssueListSearch["status"]) || undefined,
                  })
                }
              >
                <option value="">{t("issue.filter.allStatuses")}</option>
                {statusOptions.map((option) => (
                  <option key={option} value={option}>
                    {t(statusLabelKeys[option])}
                  </option>
                ))}
              </Select>
              <Select
                aria-label={t("issue.field.priority")}
                value={search.priority ?? ""}
                onChange={(event) =>
                  updateSearch({
                    priority:
                      (event.currentTarget.value as IssueListSearch["priority"]) || undefined,
                  })
                }
              >
                <option value="">{t("issue.filter.allPriorities")}</option>
                {priorityOptions.map((option) => (
                  <option key={option} value={option}>
                    {t(priorityLabelKeys[option])}
                  </option>
                ))}
              </Select>
              <Select
                aria-label={t("issue.sort.label")}
                value={`${sort}-${direction}`}
                onChange={(event) => {
                  const [field, order] = event.currentTarget.value.split("-");
                  updateSearch({ sort: field as IssueSort, direction: order as "asc" | "desc" });
                }}
              >
                <option value="updated-desc">{t("issue.sort.updatedDesc")}</option>
                <option value="updated-asc">{t("issue.sort.updatedAsc")}</option>
                <option value="priority-desc">{t("issue.sort.priorityDesc")}</option>
                <option value="priority-asc">{t("issue.sort.priorityAsc")}</option>
              </Select>
            </div>
            {hasFilters ? (
              <div className="issue-active-filters">
                <span className="muted-copy">
                  {hasContextFilters ? t("issue.filter.context") : t("issue.filter.active")}
                </span>
                <Button size="sm" variant="ghost" onClick={clearFilters}>
                  {t(hasContextFilters ? "issue.filter.clearContext" : "issue.filter.clear")}
                </Button>
              </div>
            ) : null}
            {!issuesQuery.error && issues.length > 0 ? (
              <IssueTable
                issues={issues}
                users={usersQuery.data ?? []}
                agents={agentsQuery.data ?? []}
                search={search}
                onSort={changeSort}
                scrollable
              />
            ) : (
              <QueryFeedback
                loading={issuesQuery.loading}
                error={issuesQuery.error}
                onRetry={issuesQuery.refetch}
                emptyTitle={t("work.empty")}
                emptyDescription={t("work.empty.desc")}
                emptyAction={
                  hasFilters ? (
                    <Button size="sm" variant="secondary" onClick={clearFilters}>
                      {t("issue.filter.clear")}
                    </Button>
                  ) : (
                    <Button size="sm" variant="secondary" onClick={() => setCreateOpen(true)}>
                      <Plus aria-hidden="true" size={14} />
                      {t("issue.create.submit")}
                    </Button>
                  )
                }
              />
            )}
          </TabsContent>
        </Tabs>
      </Card>
      <CreateIssueModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(issueKey) => {
          void issuesQuery.refetch();
          void router.navigate({ to: "/issues/$issueKey", params: { issueKey }, search });
        }}
      />
    </section>
  );
}
